import { useEffect, useState } from "react";
import { useDashboardViewMode } from "@/hooks/useDashboardViewMode";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { ItineraryCard } from "@/lib/itineraries/ItineraryCard";
import { ItinerariesTable } from "@/lib/itineraries/ItinerariesTable";
import { OptionFilter } from "@/lib/itineraries/OptionFilter";
import { ViewTravelRequestButton } from "@/lib/requests";
import { ListPagination } from "@/components/ui/list-pagination";
import { StatusFilter } from "@/components/ui/status-filter";
import { ItineraryCardSkeleton } from "@/lib/itineraries/ItineraryCardSkeleton";
import { useI18n } from "@/hooks/useI18n";
import {
  useDashboardItinerariesPage,
  useDashboardItineraryCounts,
  useDashboardItineraryFilterOptions,
} from "@/hooks/useDashboardLists";
import { DASHBOARD_PAGE_SIZE, ItineraryPhaseFilter, ItineraryTabProps } from "./types";

export const DashboardItinerariesTab = ({
  config,
  userRole,
  tabTotal,
  refreshKey,
  onRefresh,
  onEdit,
  onDelete,
  onDuplicate,
  onViewTravelRequest,
  onUpdateStatus,
}: ItineraryTabProps) => {
  const { t } = useI18n();
  const { value: tab, showDesignerFilter, phases, i18n } = config;
  const defaultPhase: ItineraryPhaseFilter = phases?.[0]?.value ?? 'all';

  const canFilterByDesigner = showDesignerFilter && (userRole === "admin" || userRole === "agency");
  const canFilterByClient = showDesignerFilter && userRole !== "traveler";

  const [viewMode, setViewMode] = useDashboardViewMode();
  const [designerId, setDesignerId] = useState<string | null>(null);
  const [clientEmail, setClientEmail] = useState<string | null>(null);
  const [phase, setPhase] = useState<ItineraryPhaseFilter>(defaultPhase);

  // Ogni cambio di filtro riparte da pagina 1: la pagina e' legata ai filtri che l'hanno prodotta.
  const filtersKey = JSON.stringify({ designerId, clientEmail, phase });
  const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
  const page = pageState.key === filtersKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const { data, loading, refreshing, error, version } = useDashboardItinerariesPage(
    {
      tab,
      phase: phase !== 'all' ? phase : null,
      designerId,
      clientEmail,
      limit: DASHBOARD_PAGE_SIZE,
      offset: (page - 1) * DASHBOARD_PAGE_SIZE,
    },
    refreshKey
  );

  const { data: filterOptions } = useDashboardItineraryFilterOptions(
    tab,
    refreshKey,
    canFilterByDesigner || canFilterByClient
  );

  // Le pillole di stato seguono gli altri filtri: se sto guardando un solo consulente,
  // "In corso (2)" deve riferirsi ai suoi due viaggi, non a tutti.
  const { data: phaseCounts } = useDashboardItineraryCounts(
    { designerId, clientEmail },
    refreshKey,
    !!phases
  );
  const phaseCount = (value: ItineraryPhaseFilter) =>
    value === 'all' ? phaseCounts.byTab[tab] ?? 0 : phaseCounts.byPhase[tab]?.[value] ?? 0;
  const activePhase = phases?.find((p) => p.value === phase);

  const itineraries = data.rows;

  // Cancellando l'ultimo itinerario dell'ultima pagina, la pagina non esiste piu'.
  useEffect(() => {
    if (!loading && !refreshing && !error && itineraries.length === 0 && page > 1) setPage(page - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, refreshing, error, itineraries.length, page]);
  const designerLabel = designerId
    ? filterOptions.designers.find((d) => d.value === designerId)?.label ?? ''
    : null;
  const hasFilters = !!designerId || !!clientEmail || phase !== defaultPhase;

  const clearFilters = () => {
    setDesignerId(null);
    setClientEmail(null);
    setPhase(defaultPhase);
  };

  const cardProps = {
    onEdit,
    onDelete,
    onDuplicate,
    onViewTravelRequest,
    onViewItinerary: onEdit,
    onUpdateStatus,
    onShared: onRefresh,
    isAdmin: userRole === "admin" || userRole === "agency",
    isTraveler: userRole === "traveler",
    userRole,
  };

  // Una pillola con il suo titolo (es. archiviati) ha un vuoto diverso da quello della tab:
  // "crea il tuo primo itinerario" li' non avrebbe senso.
  const emptyState = activePhase?.emptyTitleKey ? (
    <div className="text-center py-12">
      <h4 className="text-gray-600 mb-4">{t(activePhase.emptyTitleKey as any)}</h4>
      {(designerId || clientEmail) && (
        <Button variant="outline" onClick={() => { setDesignerId(null); setClientEmail(null); }} className="mt-2">
          {t('dashboard.clearFilter')}
        </Button>
      )}
    </div>
  ) : (
    <div className="text-center py-12">
      <h4 className="text-gray-600 mb-4">
        {designerLabel && i18n.emptyTitleDesignerKey
          ? t(i18n.emptyTitleDesignerKey as any, { designer: designerLabel })
          : t(i18n.emptyTitleKey as any)}
      </h4>
      {(i18n.emptyDescKey || i18n.emptyDescDesignerKey) && (
        <p className="text-gray-500 mb-4">
          {designerLabel && i18n.emptyDescDesignerKey
            ? t(i18n.emptyDescDesignerKey as any)
            : i18n.emptyDescKey
              ? t(i18n.emptyDescKey as any)
              : null}
        </p>
      )}
      {hasFilters && (
        <Button variant="outline" onClick={clearFilters} className="mt-2">
          {t('dashboard.clearFilter')}
        </Button>
      )}
    </div>
  );

  const pagination = {
    page,
    totalItems: data.totalCount,
    onPageChange: setPage,
    disabled: refreshing,
  };

  return (
    // Altezza piena: intestazione e filtri fissi, la lista riempie il resto e la
    // paginazione resta ancorata in fondo anche con pochi risultati.
    <div className="flex flex-1 flex-col min-h-0 gap-2">
      {/* Header */}
      {/* Header: a sinistra lo stato, a destra i filtri per persona e le azioni sulla vista. */}
      <div className="shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {phases && (
            <StatusFilter<ItineraryPhaseFilter>
              label={String(t('dashboard.statusFilterLabel'))}
              value={phase}
              onChange={setPhase}
              options={phases.map((p) => ({
                value: p.value,
                label: String(t(p.labelKey as any)),
                count: phaseCount(p.value),
                tone: p.tone,
              }))}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canFilterByDesigner && (
            <OptionFilter
              options={filterOptions.designers}
              selected={designerId}
              onChange={setDesignerId}
              placeholder={String(t('role.travelAdvisor'))}
              searchPlaceholder={String(t('travelRequest.searchTravelAdvisor'))}
              emptyLabel={String(t('travelRequest.noTravelAdvisorsFound'))}
              disabled={filterOptions.designers.length === 0}
            />
          )}
          {canFilterByClient && (
            <OptionFilter
              options={filterOptions.clients}
              selected={clientEmail}
              onChange={setClientEmail}
              placeholder={String(t('dashboard.filters.client'))}
              searchPlaceholder={String(t('dashboard.filters.searchClient'))}
              emptyLabel={String(t('dashboard.filters.noClients'))}
              disabled={filterOptions.clients.length === 0}
            />
          )}
          <ViewTravelRequestButton
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            disabled={tabTotal === 0}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{t('dashboard.refresh')}</span>
          </Button>
        </div>
      </div>

      {error ? (
        <div className="text-center py-12">
          <h4 className="text-red-600">{t(i18n.errorKey as any)}</h4>
          <Button onClick={onRefresh} className="mt-4">
            {t('dashboard.retry')}
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <>
          <div className="relative flex-1 min-h-0 overflow-auto">
            {loading || refreshing ? (
              // In caricamento (anche aggiorna, pagina, filtri): card scheletro con le misure
              // vere per almeno LIST_MIN_LOADING_MS (useDashboardLists), poi le card entrano animate.
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                {Array.from({ length: 8 }, (_, i) => <ItineraryCardSkeleton key={i} />)}
              </div>
            ) : itineraries.length > 0 ? (
              /* Margine interno: l'area scrolla, e senza spazio ai bordi taglierebbe l'hover
                  delle card (si alzano e si ingrandiscono) e le loro ombre. */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
                {itineraries.map((itinerary, index) => (
                  <div
                    key={`${version}-${itinerary.id}`}
                    className="animate-in fade-in zoom-in-95 duration-500"
                    style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'backwards' }}
                  >
                    <ItineraryCard itinerary={itinerary} {...cardProps} />
                  </div>
                ))}
              </div>
            ) : emptyState}
          </div>
          <ListPagination
            loading={loading}
            page={page}
            pageSize={DASHBOARD_PAGE_SIZE}
            totalCount={data.totalCount}
            onPageChange={setPage}
            disabled={refreshing}
          />
        </>
      ) : (
        <div className="flex-1 min-h-0">
          <ItinerariesTable
            itineraries={itineraries}
            {...cardProps}
            serverPagination={pagination}
            pageSize={DASHBOARD_PAGE_SIZE}
            // Skeleton anche ad ogni ricaricamento, non solo al primo
            isLoading={loading || refreshing}
            animationKey={version}
            emptyState={emptyState}
          />
        </div>
      )}
    </div>
  );
};
